/**
 * The first visit's product events (Sol analytics + round12 E5/E6; Sol onboarding-leftovers L5 A): Mixpanel, lowercase
 * snake_case completed facts, opaque ids only - never a name, email, grade, answer text, voice or token. One visit is
 * one Mixpanel identity (distinct_id = flow_session_id); the portal hands it over in the launch link (`flow`) and the
 * player hands it back on "Explore more worlds", so the funnel does not split between the two sites.
 *
 * Nothing is sent unless the build provides a web Mixpanel token (WEB_MIXPANEL_TOKEN with WEB_MIXPANEL_ENV dev or prod;
 * never the mobile MIXPANEL_TOKEN); without it every call is a no-op. These are the player's own observations
 * (source=player_client); the authoritative server versions (sign-up, badges, quiz answers) come later with source=server.
 */
import { WEB_MIXPANEL_ENV as MIXPANEL_ENV, WEB_MIXPANEL_TOKEN as MIXPANEL_TOKEN } from 'config/app.config';
import { isTrialDemo } from '../utils/trialDemo';

export const PLAYER_EVENTS = new Set([
  'onboarding_lab_loaded',
  'tour_step_viewed',
  'tour_step_completed',
  'tour_skipped',
  'explore_more_clicked',
  'quikbase_arrived',
  'quikbase_control_used',
  'portal_entered',
  'unit_selector_viewed',
  'unit_selected',
  'teleport_started',
  'teleport_completed',
  // Learning in the world after the first visit (owner 2026-10-13: "mixpanel events all around the UX and user journeys
  // and every section that they are going to use"; Sol mixpanel-coverage D1 A). Client events; the server's own
  // authoritative events come later with source=server.
  'lesson_started',
  'lesson_completed',
  'quiz_started',
  'quiz_answer_submitted',
  'quiz_completed',
  'tutor_session_started',
  'tutor_question_submitted',
  'tutor_response_completed',
  'tutor_session_ended',
  'usage_meter_exhausted',
  'player_paywall_viewed',
  'collection_store_viewed',
  'collection_item_selected',
  'collection_purchase_result',
  'badge_earned',
  'try_quik_demo_started',
  'try_quik_demo_meter_exhausted',
]);
// Any key naming a person, contact, what they said or wrote, the grade or a token is refused; has_* flags are fine.
const FORBIDDEN = /^(?!has_).*(name|email|answer_text|transcript|audio|idfa|gaid|grade|token)/i;
const FLOW_KEY = 'ql-flow-v1';
// production sends too once its build sets a token and env 'prod' (owner 13 Oct: everything to production)
const ON = Boolean(MIXPANEL_TOKEN) && ['dev', 'prototype', 'prod'].includes(MIXPANEL_ENV);

const uuid = () => {
  try {
    return crypto.randomUUID().replace(/-/g, '').slice(0, 16);
  } catch (_) {
    return Math.random().toString(16).slice(2, 18);
  }
};
const fresh = () => ({ id: `f_${uuid()}`, seq: 0, parent: null, child: null });
// One page load of the player (Sol mixpanel-coverage D1: session_id on every event).
const SESSION_ID = `s_${uuid()}`;
// The web build writes these in (see config/app.config.js); empty anywhere else.
const buildValue = read => {
  try {
    return read() || '';
  } catch (_) {
    return '';
  }
};
const APP_VERSION = buildValue(() => process.env.REACT_APP_VERSION);
const BUILD_NUMBER = buildValue(() => process.env.REACT_APP_BUILD_ID);
// Automated test runs: a driven browser, or a test browser that set this flag (browsers driven by tools that do not
// report themselves, so their runs stay out of the reports - Sol mixpanel-coverage acceptance runs qa_run=true).
export const QA_RUN_KEY = 'ql-qa-run';
const isQaRun = () => {
  if (typeof navigator !== 'undefined' && navigator.webdriver === true) return true;
  try {
    return window.localStorage.getItem(QA_RUN_KEY) === '1';
  } catch (_) {
    return false;
  }
};
const demoNow = () => {
  try {
    return isTrialDemo();
  } catch (_) {
    return false;
  }
};

/** `f_…~p_…~c_…~12` - the visit's id, its two opaque person ids and how many events it has had. */
export function formatFlow(flow) {
  return [flow.id, flow.parent || '', flow.child || '', String(flow.seq || 0)].join('~');
}
export function parseFlow(text) {
  const [id, parent, child, seq] = String(text || '').split('~');
  if (!/^f_[0-9a-f]{8,32}$/.test(id || '')) return null;
  const clean = value => (/^[pc]_[0-9a-f]{8,32}$/.test(value || '') ? value : null);
  return { id, parent: clean(parent), child: clean(child), seq: Number.isFinite(Number(seq)) ? Number(seq) : 0 };
}

let flow = null;
function current() {
  if (flow) return flow;
  let fromLink = null;
  let saved = null;
  try {
    fromLink = parseFlow(new URLSearchParams(window.location.search).get('flow'));
  } catch (_) {
    fromLink = null;
  }
  try {
    saved = parseFlow(window.localStorage.getItem(FLOW_KEY));
  } catch (_) {
    saved = null;
  }
  flow = fromLink || saved || fresh();
  keep();
  return flow;
}
function keep() {
  try {
    window.localStorage.setItem(FLOW_KEY, formatFlow(flow));
  } catch (_) {
    // private windows: the visit still counts within this page
  }
}

/** The visit, for the link back to the portal. */
export const flowParam = () => formatFlow(current());

/** Records one completed fact. Never throws into the product: a bad event is dropped with a warning. */
export function track(event, props = {}) {
  if (!PLAYER_EVENTS.has(event)) {
    console.warn(`[analytics] ${event} is not in the tracking plan`);
    return null;
  }
  const bad = Object.keys(props).find(key => FORBIDDEN.test(key));
  if (bad) {
    console.warn(`[analytics] ${event} dropped: forbidden property ${bad}`);
    return null;
  }
  const visit = current();
  visit.seq += 1;
  keep();
  const properties = {
    token: MIXPANEL_TOKEN,
    distinct_id: visit.id,
    time: Date.now(),
    $insert_id: uuid(),
    flow_session_id: visit.id,
    flow_seq: visit.seq,
    side: 'C',
    surface: 'player',
    env: MIXPANEL_ENV,
    // Common to every player event (Sol mixpanel-coverage D1 A)
    schema_version: 1,
    source: 'player_client',
    environment: MIXPANEL_ENV,
    app_version: APP_VERSION,
    build_number: BUILD_NUMBER,
    platform: 'web',
    is_demo: demoNow(),
    session_id: SESSION_ID,
    qa_run: isQaRun(),
    ...(visit.parent ? { parent_id: visit.parent } : {}),
    ...(visit.child ? { child_id: visit.child, child_profile_id: visit.child } : {}),
    ...props,
  };
  if (ON && typeof fetch === 'function') {
    fetch('https://api-js.mixpanel.com/track?ip=0', {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
      body: `data=${encodeURIComponent(JSON.stringify([{ event, properties }]))}`,
      keepalive: true,
    }).catch(() => {});
  }
  return { event, properties, sent: ON };
}
